import { normalizeAmount } from "@frontend/utils/amount";

import type {
  AccountantValues,
  AddressValues,
  ClientContactValues,
  ClientValues,
} from "../schemas/client";
import type { ConditionsValues } from "../schemas/conditions";
import type {
  AccountantInput,
  Client,
  ClientContactInput,
  ClientInput,
  CommercialCondition,
  CommercialConditionInput,
  Contact,
  PriceInput,
  TypedAddress,
} from "../types";

function blankToNull(value: string | null): string | null {
  return null === value || "" === value.trim() ? null : value;
}

export function toClientInput(values: ClientValues): ClientInput {
  return {
    lockVersion: values.lockVersion,
    contact: {
      id: values.contactId ?? 0,
      title: blankToNull(values.title),
      firstName: blankToNull(values.firstName),
      lastName: blankToNull(values.lastName),
      isOrganization: values.isOrganization,
      organization: blankToNull(values.organization),
      organizationSubName: values.isOrganization
        ? (values.organizationSubName ?? "").trim()
        : null,
      phones: values.phones
        .filter((phone) => "" !== phone.number.trim())
        .map((phone) => ({ place: phone.place, number: phone.number.trim() })),
      emails: values.emails
        .filter((email) => "" !== email.address.trim())
        .map((email) => ({
          place: email.place,
          address: email.address.trim(),
        })),
      urls: values.urls
        .filter((url) => "" !== url.url.trim())
        .map((url) => ({ place: url.place, url: url.url.trim() })),
      typedAddresses: values.addresses.map(toAddressInput),
    },
    statusId: values.statusId,
    typeId: values.typeId,
    fieldId: values.fieldId,
    regionId: values.regionId,
    provenanceId: values.provenanceId,
    provenanceNote: blankToNull(values.provenanceNote),
    provenanceClientId: values.provenanceClientId,
    provenanceEmployeeId: values.provenanceEmployeeId,
    comments: blankToNull(values.comments),
    contacts: values.contacts.map(toClientContactInput),
    accountants: values.accountants.map(toAccountantInput),
  };
}

function toAddressInput(address: AddressValues): TypedAddress {
  return {
    typedAddressId: address.typedAddressId,
    place: address.place,
    street: blankToNull(address.street),
    extendedAddress: blankToNull(address.extendedAddress),
    postOfficeBox: blankToNull(address.postOfficeBox),
    postalCode: blankToNull(address.postalCode),
    city: blankToNull(address.city),
    state: blankToNull(address.state),
    country: blankToNull(address.country),
  };
}

function toClientContactInput(
  contact: ClientContactValues,
): ClientContactInput {
  return {
    id: contact.id,
    contactId: contact.contactId,
    typeId: contact.typeId,
  };
}

function toAccountantInput(accountant: AccountantValues): AccountantInput {
  return {
    id: accountant.id,
    employeeId: accountant.employeeId,
    typeId: accountant.typeId,
  };
}

export function toClientValues(
  client: Client | null,
  contact: Contact | null,
): ClientValues {
  return {
    lockVersion: client?.lockVersion ?? null,
    contactId: contact?.id ?? client?.contactId ?? null,
    title: contact?.title ?? null,
    firstName: contact?.firstName ?? null,
    lastName: contact?.lastName ?? null,
    isOrganization: contact?.isOrganization ?? client?.isOrganization ?? false,
    organization: contact?.organization ?? null,
    organizationSubName: contact?.organizationSubName ?? null,
    phones: (contact?.phones ?? []).map((phone) => ({
      place: phone.place,
      number: phone.number,
    })),
    emails: (contact?.emails ?? []).map((email) => ({
      place: email.place,
      address: email.address,
    })),
    urls: (contact?.urls ?? []).map((url) => ({
      place: url.place,
      url: url.url,
    })),
    addresses: (contact?.typedAddresses ?? []).map((address) => ({
      typedAddressId: address.typedAddressId,
      place: address.place ?? "",
      street: address.street,
      extendedAddress: address.extendedAddress,
      postOfficeBox: address.postOfficeBox,
      postalCode: address.postalCode,
      city: address.city,
      state: address.state,
      country: address.country,
    })),
    statusId: client?.statusId ?? null,
    typeId: client?.typeId ?? null,
    fieldId: client?.fieldId ?? null,
    regionId: client?.regionId ?? null,
    provenanceId: client?.provenanceId ?? null,
    provenanceNote: client?.provenanceNote ?? null,
    provenanceClientId: client?.provenanceClientId ?? null,
    provenanceEmployeeId: client?.provenanceEmployeeId ?? null,
    comments: client?.comments ?? null,
    contacts: (client?.contacts ?? []).map((contactRow) => ({
      id: contactRow.id,
      contactId: contactRow.contactId,
      typeId: contactRow.typeId,
    })),
    accountants: (client?.accountants ?? []).map((accountant) => ({
      id: accountant.id,
      employeeId: accountant.employeeId,
      typeId: accountant.typeId,
    })),
  };
}

export function toConditionsInput(
  values: ConditionsValues,
): CommercialConditionInput {
  const prices: PriceInput[] = [];

  for (const price of values.prices) {
    const granted = blankToNull(price.price);

    if (null !== granted) {
      prices.push({
        priceTypeId: price.priceTypeId,
        price: normalizeAmount(granted),
      });
    }
  }

  return {
    lockVersion: values.lockVersion,
    paymentTypeId: values.paymentTypeId,
    paymentEmail: blankToNull(values.paymentEmail),
    paymentEmailCopyTo: blankToNull(values.paymentEmailCopyTo),
    paymentTargetId: values.paymentTargetId,
    subscriptionPaymentPeriodId: values.subscriptionPaymentPeriodId,
    interventionPaymentPeriodId: values.interventionPaymentPeriodId,
    subscriptionBillingTimeId: values.subscriptionBillingTimeId,
    riskClassId: values.riskClassId,
    ongoingAuthorized: values.ongoingAuthorized,
    priceRevisionActivation: values.priceRevisionActivation,
    prices,
  };
}

export function toConditionsValues(
  conditions: CommercialCondition,
  emailRequiredFor: number[],
): ConditionsValues {
  return {
    lockVersion: conditions.lockVersion,
    paymentTypeId: conditions.paymentTypeId,
    paymentEmail: conditions.paymentEmail,
    paymentEmailCopyTo: conditions.paymentEmailCopyTo,
    paymentTargetId: conditions.paymentTargetId,
    subscriptionPaymentPeriodId: conditions.subscriptionPaymentPeriodId,
    interventionPaymentPeriodId: conditions.interventionPaymentPeriodId,
    subscriptionBillingTimeId: conditions.subscriptionBillingTimeId,
    riskClassId: conditions.riskClassId,
    ongoingAuthorized: conditions.ongoingAuthorized,
    priceRevisionActivation: conditions.priceRevisionActivation,
    prices: conditions.prices.map((price) => ({
      priceTypeId: price.priceTypeId,
      price: price.price,
    })),
    emailRequiredFor,
  };
}
